import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from 'services/api';
import { toast } from 'react-toastify';
import { flattenQuery } from 'helpers/flattenQuery';
import type { FilterConfig } from 'hooks/useListQuery';
import { extractErrorMessage } from 'helpers/extractErrorMessage';
// Single source of truth for the recurrence wire shape (mirrors nap-backend).
// This list endpoint isn't boundary-validated, but it returns the same
// `recurrences` rules as the validated single-record endpoint, so we reuse
// their canonical type rather than maintaining a second copy that can drift.
import type { Recurrence, RecurrenceUnit } from 'api/recurrence_definitions/endpoints/types';

export type { Recurrence, RecurrenceUnit };

export interface RecurrenceDefinition {
  recurrence_definition_id?: number;
  name: string;
  description: string;
  zone_name: string;
  resubmit_on_error?: boolean;
  // A definition owns one or more recurrence rules. `recurrence_display` is the
  // backend's pre-formatted summary of them for the list column.
  recurrences: readonly Recurrence[];
  recurrence_display?: string;
  created_at?: string;
  updated_at?: string;
}

interface PaginationState {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

export interface HeaderConfig {
  header_key: string;
  header_type: string;
  label: string;
}

interface LoadingState {
  fetchRecurrenceDefinitions: boolean;
  deleteRecurrenceDefinition: boolean;
}

interface State {
  nodes: RecurrenceDefinition[];
  items: RecurrenceDefinition[];
  pagination: PaginationState;
  status: string | null;
  error: string | null;
  config: HeaderConfig[];
  filtersConfig: FilterConfig[];
  loading: LoadingState;
}

const initialState: State = {
  nodes: [],
  items: [],
  pagination: {
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: 2000,
  },
  status: null,
  error: null,
  config: [],
  filtersConfig: [],
  loading: {
    fetchRecurrenceDefinitions: false,
    deleteRecurrenceDefinition: false,
  },
};

export const fetchRecurrenceDefinitions = createAsyncThunk('recurrenceDefinitions/fetch', async (params: Record<string, any> = {}) => {
  const cleanParams = {
    ...params,
    sortColumn: params.sortColumn || 'created_at',
    sortOrder: (params.sortOrder || 'ASC').toUpperCase(),
    page: params.page || 1,
    limit: params.limit || 2000,
  };

  const res = await api.get('/recurrence_definitions', {
    params: flattenQuery(cleanParams),
  });
  return res.data;
});

export const deleteRecurrenceDefinition = createAsyncThunk(
  'recurrenceDefinitions/delete',
  async ({ zone_name, name }: { zone_name: string; name: string }, { rejectWithValue }) => {
    try {
      await api.delete(`/recurrence_definitions/${zone_name}/${name}`);
      return { zone_name, name };
    } catch (error: any) {
      return rejectWithValue(extractErrorMessage(error.response?.data, error.message));
    }
  }
);

const recurrenceDefinitionsSlice = createSlice({
  name: 'recurrenceDefinitions',
  initialState,
  reducers: {},
  extraReducers: (b) => {
    // fetchRecurrenceDefinitions
    b.addCase(fetchRecurrenceDefinitions.pending, (state) => {
      state.status = 'loading';
      state.error = null;
      state.loading.fetchRecurrenceDefinitions = true;
    });
    b.addCase(fetchRecurrenceDefinitions.fulfilled, (state, action) => {
      const { nodes = [], pagination, config = [], filtersConfig = [] } = action.payload || {};

      state.nodes = nodes;
      state.items = nodes;
      state.config = config;
      state.filtersConfig = filtersConfig;

      state.pagination.totalItems = pagination?.total_records ?? 0;
      state.pagination.totalPages = pagination?.total_pages ?? 0;
      state.pagination.pageSize = pagination?.limit ?? state.pagination.pageSize;
      const cp = pagination?.current_page ?? 1;
      const tp = pagination?.total_pages ?? 0;
      state.pagination.currentPage = cp > tp && tp > 0 ? 1 : cp;

      state.status = 'succeeded';
      state.loading.fetchRecurrenceDefinitions = false;
    });
    b.addCase(fetchRecurrenceDefinitions.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to fetch recurrence definitions';
      state.loading.fetchRecurrenceDefinitions = false;
    });

    // deleteRecurrenceDefinition
    b.addCase(deleteRecurrenceDefinition.pending, (state) => {
      state.loading.deleteRecurrenceDefinition = true;
    });
    b.addCase(deleteRecurrenceDefinition.fulfilled, (state, action) => {
      state.loading.deleteRecurrenceDefinition = false;
      state.nodes = state.nodes.filter((n) => !(n.zone_name === action.payload.zone_name && n.name === action.payload.name));
      state.items = state.nodes;
    });
    b.addCase(deleteRecurrenceDefinition.rejected, (state, action) => {
      state.loading.deleteRecurrenceDefinition = false;
      const msg = (action.payload as { error?: string } | undefined)?.error || action.error.message || 'An error occurred';
      state.error = msg;
      const toastId = 'data-failed-to-delete-recurrence-definition';
      if (!toast.isActive(toastId)) toast.error(msg, { toastId });
    });
  },
});

export default recurrenceDefinitionsSlice.reducer;
